import { DialogElement } from 'theme';

// Shopify App Proxy preserves the path after its storefront root, so this
// must match the complete upstream gateway path on api-test.sigencloud.com.
const RECOMMENDATION_PATH = '/mall/sigenmate_calculator/recommendation';
const REQUEST_TIMEOUT_MS = 10000;
const MOBILE_HANDOFF_PARAM = 'calculator_handoff';
const MOBILE_HANDOFF_PREFIX = 'savings-calculator-handoff:';

if (!window.customElements.get('savings-calculator')) {
  class SavingsCalculator extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 1;
      this.toastTimer = null;
      this.carouselFrame = null;
      this.overflowTooltipFrame = null;
      this.activeOverflowTooltipTarget = null;
      this.overflowTooltip = null;
      this.carouselObserver = null;
      this.savingsAbortController = null;
      this.recommendationAbortController = null;
      this.equipmentWithBundleStatus = [];
      this.customSetupRequest = null;
      this.lastRecommendationPayload = null;
      this.cartSubmitting = false;
    }

    connectedCallback() {
      if (this.savingsAbortController) {
        return;
      }

      this.savingsAbortController = new AbortController();
      const signal = this.savingsAbortController.signal;

      if (this.dataset.displayMode === 'page') {
        const backLink = this.closest('.savings-calculator-page')?.querySelector('[data-calculator-page-back]');
        if (backLink && document.referrer) {
          try {
            const previousPage = new URL(document.referrer);
            if (previousPage.origin === window.location.origin && previousPage.pathname !== window.location.pathname) {
              backLink.href = previousPage.href;
            }
          } catch {
            // Keep the storefront home link when no valid return page is available.
          }
        }
      }

      this.form = this.querySelector('.savings-calculator-form');
      this.progress = this.querySelector('[data-progress]');
      this.progressValue = this.querySelector('[data-progress-value]');
      this.footer = this.querySelector('[data-form-footer]');
      this.backButton = this.querySelector('[data-back]');
      this.continueButton = this.querySelector('[data-continue]');
      this.toast = this.querySelector('[data-toast]');
      this.productCarousel = this.querySelector('[data-product-carousel]');
      this.carouselPrevious = this.querySelector('[data-carousel-previous]');
      this.carouselNext = this.querySelector('[data-carousel-next]');
      this.addToCartButton = this.querySelector('[data-add-setup-to-cart]');
      this.setupDetailsLink = this.querySelector('[data-setup-details]');
      this.cartStatus = this.querySelector('[data-cart-status]');
      this.recommendationErrorDescription = this.querySelector('[data-recommendation-error-description]');
      this.defaultRecommendationErrorDescription = this.recommendationErrorDescription?.textContent.trim() || '';
      this.equipmentCardTemplates = Array.from(this.querySelectorAll('.savings-calculator-result__product'))
        .map((card) => card.cloneNode(true));
      this.overflowTooltip = document.createElement('div');
      this.overflowTooltip.className = 'savings-calculator-overflow-tooltip';
      this.overflowTooltip.setAttribute('role', 'tooltip');
      this.overflowTooltip.hidden = true;
      document.body.append(this.overflowTooltip);

      this.form?.addEventListener('input', (event) => {
        if (event.target.matches('[data-custom-quantity]')) {
          this.syncCustomSetup(event.target);
          return;
        }
        if (event.target.matches('input:not([type="radio"])')) {
          event.target.dataset.touched = 'true';
          this.validateField(event.target, true);
        }

        this.updateContinueState();
      }, { signal });
      this.form?.addEventListener('change', (event) => {
        if (event.target.matches('[name="country_code"]')) {
          const postcode = this.querySelector('[name="postcode"]');
          this.syncPostcodeValidation(Boolean(postcode?.dataset.touched || postcode?.value.trim()));
        }

        if (event.target.matches('[name="type"]')) {
          this.syncConditionalFields({ resetTariffValidation: true });
        } else if (event.target.matches('[name="has_existing_solar"]')) {
          this.syncConditionalFields();
        }

        if (event.target.matches('input:not([type="radio"])')) {
          event.target.dataset.touched = 'true';
          this.validateField(event.target, true);
        }

        this.updateContinueState();
      }, { signal });
      this.form?.addEventListener('blur', (event) => {
        const nextTarget = event.relatedTarget;
        const isModeSwitch = nextTarget instanceof Element
          && nextTarget.closest('[data-consumption-mode], input[name="type"]');

        if (event.target.matches('input:not([type="radio"])') && !isModeSwitch) {
          event.target.dataset.touched = 'true';
          this.validateField(event.target, true);
        }
      }, { capture: true, signal });
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.goForward();
      }, { signal });
      this.backButton?.addEventListener('click', () => this.goBack(), { signal });
      this.continueButton?.addEventListener('click', () => this.goForward(), { signal });
      this.querySelector('[data-customize]')?.addEventListener('click', () => {
        this.showCustomSetup();
      }, { signal });
      this.querySelector('[data-system-recommendation]')?.addEventListener('click', () => this.setStep(5), { signal });
      this.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const field = button.closest('[data-quantity-card]')?.querySelector('[data-custom-quantity]');
          if (!field) return;
          const quantity = Number(field.value);
          const step = Number(field.step) || 1;
          const current = Number.isFinite(quantity) ? quantity : 0;
          const direction = Number(button.dataset.quantityChange);
          const next = (direction > 0 ? Math.floor(current / step) + 1 : Math.ceil(current / step) - 1) * step;
          field.value = String(Math.min(Number(field.max), Math.max(0, next)));
          field.dispatchEvent(new Event('input', { bubbles: true }));
        }, { signal });
      });
      this.querySelector('[data-retry]')?.addEventListener('click', () => this.retryRecommendation(), { signal });
      const feedback = this.querySelector('[data-calculator-feedback]');
      this.querySelector('[data-feedback-close]')?.addEventListener('click', () => this.closeCalculationFeedback(), { signal });
      this.querySelector('[data-feedback-action]')?.addEventListener('click', () => this.handleFeedbackAction(), { signal });
      feedback?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeCalculationFeedback();
      }, { signal });
      feedback?.addEventListener('keydown', (event) => {
        // The enclosing theme dialog must stay open when feedback is dismissed.
        if (event.key === 'Escape') event.stopPropagation();
      }, { signal });
      this.querySelector('[data-edit]')?.addEventListener('click', () => this.setStep(1), { signal });
      this.addToCartButton?.addEventListener('click', () => this.addSetupToCart(), { signal });

      this.querySelectorAll('[data-consumption-mode]').forEach((button) => {
        button.addEventListener('click', () => {
          this.syncConsumptionFields(button.dataset.consumptionMode, true, true);
          this.updateContinueState();
        }, { signal });
      });

      this.querySelectorAll('[data-coming-soon]').forEach((button) => {
        button.addEventListener('click', () => this.showToast(this.getString('comingSoon')), { signal });
      });

      this.carouselPrevious?.addEventListener('click', () => this.moveProductCarousel(-1), { signal });
      this.carouselNext?.addEventListener('click', () => this.moveProductCarousel(1), { signal });
      this.productCarousel?.addEventListener('scroll', () => this.scheduleCarouselSync(), { passive: true, signal });
      this.productCarousel?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
          return;
        }

        event.preventDefault();
        this.moveProductCarousel(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      this.addEventListener('pointerover', (event) => this.handleOverflowTooltipEnter(event), { signal });
      this.addEventListener('pointerout', (event) => this.handleOverflowTooltipLeave(event), { signal });
      this.addEventListener('focusin', (event) => this.handleOverflowTooltipFocus(event), { signal });
      this.addEventListener('focusout', (event) => this.handleOverflowTooltipBlur(event), { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.hideOverflowTooltip();
        }
      }, { signal });
      this.addEventListener('scroll', () => this.hideOverflowTooltip(), { capture: true, passive: true, signal });
      window.addEventListener('resize', () => {
        this.hideOverflowTooltip();
        this.scheduleCarouselSync();
        this.scheduleOverflowTooltipSync();
      }, { signal });
      window.addEventListener('scroll', () => this.hideOverflowTooltip(), { passive: true, signal });

      if (this.productCarousel) {
        this.carouselObserver = new MutationObserver(() => this.scheduleCarouselSync());
        this.carouselObserver.observe(this.productCarousel, { childList: true });
      }

      this.resetCalculator();
      this.restoreMobileHandoff();
    }

    restoreMobileHandoff() {
      if (this.dataset.displayMode !== 'page' || !this.form) return;

      const url = new URL(window.location.href);
      const token = url.searchParams.get(MOBILE_HANDOFF_PARAM);
      if (!token) return;

      url.searchParams.delete(MOBILE_HANDOFF_PARAM);
      window.history.replaceState(window.history.state, '', url.href);
      try {
        const key = MOBILE_HANDOFF_PREFIX + token;
        const saved = window.sessionStorage.getItem(key);
        window.sessionStorage.removeItem(key);
        if (!saved) return;
        const state = JSON.parse(saved);
        if (state.url !== url.href || Date.now() - state.savedAt > 60000) return;

        for (const field of this.form.elements) {
          if (!Object.hasOwn(state.fields, field.name)) continue;
          if (field.type === 'radio') {
            field.checked = field.value === state.fields[field.name];
          } else {
            field.value = state.fields[field.name];
          }
        }
        this.syncPostcodeValidation(false);
        this.syncConsumptionFields(state.fields.input_mode);
        this.syncConditionalFields();
        this.setStep(state.step);
        if (state.view === 'loading' || state.view === 'result') this.calculate();
      } catch {
        // Storage can be unavailable in private browsing; the page remains usable.
      }
    }

    disconnectedCallback() {
      this.querySelector('[data-calculator-feedback]')?.close();
      window.clearTimeout(this.toastTimer);
      window.cancelAnimationFrame(this.carouselFrame);
      window.cancelAnimationFrame(this.overflowTooltipFrame);
      this.carouselFrame = null;
      this.overflowTooltipFrame = null;
      this.overflowTooltip?.remove();
      this.overflowTooltip = null;
      this.activeOverflowTooltipTarget = null;
      this.carouselObserver?.disconnect();
      this.carouselObserver = null;
      this.recommendationAbortController?.abort();
      this.recommendationAbortController = null;
      this.savingsAbortController?.abort();
      this.savingsAbortController = null;
    }

    resetCalculator() {
      this.closeCalculationFeedback();
      this.lastRecommendationPayload = null;
      this.customSetupRequest = null;
      window.clearTimeout(this.toastTimer);
      this.recommendationAbortController?.abort();
      this.recommendationAbortController = null;
      this.equipmentWithBundleStatus = [];
      this.cartSubmitting = false;
      this.form?.reset();
      this.syncPostcodeValidation(false);
      this.syncConsumptionFields();
      this.syncConditionalFields();
      this.form?.querySelectorAll('input:not([type="radio"])').forEach((field) => {
        delete field.dataset.touched;
        this.validateField(field, false);
      });
      this.updateCartActions();
      this.updateSetupDetailsLink();
      this.hideCartStatus();
      this.hideToast();
      this.resetRecommendationError();
      this.setStep(1);
    }

    setStep(step) {
      this.recommendationAbortController?.abort();
      this.recommendationAbortController = null;
      this.customSetupRequest = null;
      this.currentStep = Math.min(5, Math.max(1, Number(step) || 1));
      delete this.dataset.currentView;
      this.hideToast();

      this.querySelectorAll('[data-step]').forEach((element) => {
        element.hidden = Number(element.dataset.step) !== this.currentStep;
      });

      this.querySelectorAll('[data-view]').forEach((element) => {
        element.hidden = true;
      });

      if (this.progress) {
        this.progress.hidden = false;
        this.progress.textContent = `${this.currentStep} / 5`;
      }

      if (this.progressValue) {
        this.progressValue.parentElement.hidden = false;
        this.progressValue.style.width = `${this.currentStep * 20}%`;
      }

      if (this.footer) {
        this.footer.hidden = false;
      }

      if (this.backButton) {
        this.backButton.hidden = this.currentStep === 1;
      }

      this.updateContinueState();
      this.resetDesktopPageScroll();
      this.querySelector(`[data-step="${this.currentStep}"] h3`)?.focus({ preventScroll: true });
    }

    resetDesktopPageScroll() {
      if (this.closest('.savings-calculator-page--desktop') && window.matchMedia('(min-width: 700px)').matches) {
        const body = this.querySelector('.savings-calculator-dialog__body');
        if (body) {
          body.scrollTop = 0;
        }
      }
    }

    showCustomSetup() {
      this.recommendationAbortController?.abort();
      this.recommendationAbortController = null;
      const customSetup = this.querySelector('[data-view="custom-setup"]');
      if (!customSetup) return;
      this.customQuantityNotice = '';
      const hasExistingSolar = this.getSelected('has_existing_solar')?.value === 'true';
      customSetup.dataset.existingSolar = String(hasExistingSolar);
      const panelsCard = customSetup.querySelector('[data-quantity-card="panels"]');
      if (panelsCard) {
        panelsCard.hidden = hasExistingSolar;
        panelsCard.querySelectorAll('input, button').forEach((control) => {
          control.disabled = hasExistingSolar;
        });
      }
      const existingSolarNote = customSetup.querySelector('[data-custom-existing-solar-note]');
      if (existingSolarNote) existingSolarNote.hidden = !hasExistingSolar;
      this.currentStep = 5;
      this.hideToast();
      this.showView('custom-setup');
      if (this.progress) {
        this.progress.hidden = false;
        this.progress.textContent = '5 / 5';
      }
      if (this.progressValue) {
        this.progressValue.parentElement.hidden = false;
        this.progressValue.style.width = '100%';
      }
      if (this.footer) this.footer.hidden = false;
      if (this.backButton) this.backButton.hidden = false;
      this.updateContinueState();
      this.querySelector('[data-view="custom-setup"] h3')?.focus({ preventScroll: true });
    }

    showView(name) {
      this.resetDesktopPageScroll();
      this.dataset.currentView = name;
      this.querySelectorAll('[data-step]').forEach((element) => {
        element.hidden = true;
      });
      this.querySelectorAll('[data-view]').forEach((element) => {
        element.hidden = element.dataset.view !== name;
      });

      if (this.progress) {
        const showCompletedProgress = this.dataset.displayMode === 'page' && name === 'loading';
        this.progress.hidden = !showCompletedProgress;

        if (showCompletedProgress) {
          this.progress.textContent = '5 / 5';
        }
      }

      if (this.progressValue) {
        const showCompletedProgress = this.dataset.displayMode === 'page' && name === 'loading';
        this.progressValue.parentElement.hidden = !showCompletedProgress;
        this.progressValue.style.width = showCompletedProgress ? '100%' : `${this.currentStep * 20}%`;
      }

      if (this.footer) {
        this.footer.hidden = true;
      }

      if (name === 'result') {
        this.scheduleCarouselSync(true);
        this.scheduleOverflowTooltipSync();

        document.fonts?.ready.then(() => {
          if (this.isConnected && this.dataset.currentView === 'result') {
            this.scheduleOverflowTooltipSync();
          }
        });
      }
    }

    scheduleCarouselSync(reset = false) {
      window.cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = window.requestAnimationFrame(() => {
        this.carouselFrame = null;
        this.syncProductCarousel(reset);
      });
    }

    getVisibleProductCards() {
      return Array.from(this.productCarousel?.querySelectorAll('.savings-calculator-result__product') || [])
        .filter((card) => !card.hidden);
    }

    syncProductCarousel(reset = false) {
      if (!this.productCarousel || !this.carouselPrevious || !this.carouselNext) {
        return;
      }

      if (reset) {
        this.productCarousel.scrollTo({ left: 0, behavior: 'auto' });
      }

      const cards = this.getVisibleProductCards();
      const hasOverflow = cards.length > 3 && this.productCarousel.scrollWidth - this.productCarousel.clientWidth > 1;
      const atStart = this.productCarousel.scrollLeft <= 1;
      const atEnd = this.productCarousel.scrollLeft + this.productCarousel.clientWidth >= this.productCarousel.scrollWidth - 1;

      this.carouselPrevious.hidden = !hasOverflow;
      this.carouselNext.hidden = !hasOverflow;
      this.carouselPrevious.disabled = !hasOverflow || atStart;
      this.carouselNext.disabled = !hasOverflow || atEnd;
    }

    moveProductCarousel(direction) {
      if (!this.productCarousel) {
        return;
      }

      const card = this.getVisibleProductCards()[0];
      if (!card) {
        return;
      }

      const styles = window.getComputedStyle(this.productCarousel);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const distance = card.getBoundingClientRect().width + gap;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      this.productCarousel.scrollBy({ left: direction * distance, behavior });
    }

    goBack() {
      if (this.dataset.currentView === 'custom-setup') {
        this.setStep(5);
        return;
      }
      if (this.currentStep > 1) {
        this.setStep(this.currentStep - 1);
      }
    }

    goForward() {
      if (this.dataset.currentView === 'custom-setup') {
        if (!this.syncCustomSetup()) {
          this.getCurrentFields().find((field) => !field.checkValidity())?.reportValidity();
          return;
        }
        const fields = this.getCurrentFields();
        const invalidField = fields.find((field) => !field.checkValidity());
        if (invalidField) {
          invalidField.reportValidity();
          return;
        }
        const customSetup = {
          sigenmate: Number(this.querySelector('[name="custom_sigenmate_quantity"]').value),
          battery: Number(this.querySelector('[name="custom_battery_quantity"]').value)
        };
        if (this.getSelected('has_existing_solar')?.value !== 'true') {
          customSetup.pv_panels = Number(this.querySelector('[name="custom_panels_quantity"]').value);
        }
        this.calculate(customSetup);
        return;
      }
      const currentFields = this.getCurrentFields();

      currentFields.forEach((field) => {
        field.dataset.touched = 'true';
        this.validateField(field, true);
      });

      if (!this.isCurrentStepValid()) {
        const invalidField = currentFields.find((field) => !field.checkValidity() || field.value.trim() === '');
        if (invalidField) {
          invalidField.focus({ preventScroll: true });
        }
        return;
      }

      if (this.currentStep < 5) {
        this.setStep(this.currentStep + 1);
        return;
      }

      // System recommendations must never reuse quantities from a custom simulation.
      this.calculate(null);
    }

    retryRecommendation() {
      return this.calculate(this.customSetupRequest, this.lastRecommendationPayload);
    }

    async calculate(customSetup = this.customSetupRequest, retryPayload = null) {
      this.closeCalculationFeedback();
      this.customSetupRequest = retryPayload ? retryPayload.custom_setup || null : customSetup;
      const payload = retryPayload || this.buildRecommendationPayload();
      this.lastRecommendationPayload = JSON.parse(JSON.stringify(payload));
      this.resetRecommendationError();
      this.showView('loading');
      this.equipmentWithBundleStatus = [];
      this.hideCartStatus();
      this.updateCartActions();
      this.updateSetupDetailsLink();
      this.recommendationAbortController?.abort();
      const controller = new AbortController();
      this.recommendationAbortController = controller;
      let timedOut = false;
      let phase = 'calculation';
      let timeoutId;
      const startTimeout = () => {
        timeoutId = window.setTimeout(() => { timedOut = true; controller.abort(); }, REQUEST_TIMEOUT_MS);
      };
      startTimeout();
      try {
        const data = await this.requestRecommendation(payload, controller.signal);
        if (this.recommendationAbortController !== controller) return;
        if (controller.signal.aborted) throw new Error('Request cancelled');
        window.clearTimeout(timeoutId);
        phase = 'products';
        startTimeout();
        const equipment = await this.enrichEquipmentWithBundleStatus(data.equipment, controller.signal);
        if (this.recommendationAbortController !== controller) return;
        if (controller.signal.aborted) throw new Error('Request cancelled');
        this.equipmentWithBundleStatus = equipment;
        this.renderRecommendation(data, equipment);
        this.renderResultAnswers();
        this.showView('result');
      } catch (cause) {
        if (this.recommendationAbortController !== controller || (controller.signal.aborted && !timedOut)) return;
        const error = timedOut
          ? this.createRecommendationError('REQUEST_TIMEOUT', 'Request timed out', { category: 'service', phase, retryable: true, cause })
          : cause;
        console.error('[SigenMate calculator] Recommendation preparation failed', error);
        // Restore form helpers only after this request has relinquished ownership.
        this.recommendationAbortController = null;
        this.showCalculationFailure(error);
      } finally {
        window.clearTimeout(timeoutId);
        if (this.recommendationAbortController === controller) this.recommendationAbortController = null;
      }
    }

    closeCalculationFeedback() {
      const feedback = this.querySelector('[data-calculator-feedback]');
      if (!feedback?.open) return;
      feedback.close();
      if (this.feedbackParentDialog?.open) this.feedbackParentDialog.focusTrap.unpause();
      this.feedbackParentDialog = null;
      this.continueButton?.focus({ preventScroll: true });
    }

    handleFeedbackAction() {
      this.closeCalculationFeedback();
      if (this.feedbackAction === 'retry') {
        this.retryRecommendation();
        return;
      }
      if (this.feedbackAction === 'field') {
        const field = this.feedbackError?.errors?.find((item) => typeof item?.field === 'string')?.field || '';
        const step = field.startsWith('location.') ? 1 : field.startsWith('consumption.') ? 2
          : field.startsWith('tariff.') ? 3 : field.startsWith('solar.') ? 4 : 5;
        if (field.startsWith('custom_setup.') && this.lastRecommendationPayload?.custom_setup) this.showCustomSetup();
        else this.setStep(step);
        return;
      }
      if (this.lastRecommendationPayload?.custom_setup) this.showCustomSetup();
      else this.setStep(1);
    }

    showCalculationFailure(error) {
      this.renderRecommendationError(error);
      const feedback = this.querySelector('[data-calculator-feedback]');
      if (!feedback?.showModal) {
        this.showView('error');
        return;
      }
      if (this.lastRecommendationPayload?.custom_setup) this.showCustomSetup();
      else this.setStep(5);
      this.feedbackError = error;
      const custom = Boolean(this.lastRecommendationPayload?.custom_setup);
      const customCalculationFailed = custom && error.code === 'CALCULATION_FAILED';
      const category = error.category;
      this.feedbackAction = customCalculationFailed ? 'edit' : category === 'validation' ? 'field'
        : category === 'no-recommendation' || category === 'no-combination' ? 'edit' : 'retry';
      const titleKey = customCalculationFailed ? 'feedbackCustomTitle'
        : category === 'no-recommendation' ? 'feedbackNoRecommendationTitle'
        : category === 'validation' ? 'feedbackValidationTitle'
        : custom ? 'feedbackCustomTitle' : 'feedbackTitle';
      const descriptionKey = customCalculationFailed ? 'feedbackCustomDescription'
        : category === 'no-recommendation' ? 'feedbackNoRecommendationDescription'
        : category === 'validation' ? 'feedbackValidationDescription'
        : category === 'no-combination' ? 'feedbackCustomDescription'
        : category === 'service' ? 'feedbackServiceDescription' : 'feedbackDescription';
      this.querySelector('[data-feedback-title]').textContent = this.getString(titleKey);
      this.querySelector('[data-feedback-description]').textContent = this.getString(descriptionKey);
      this.querySelector('[data-feedback-action]').textContent = this.getString(this.feedbackAction === 'retry' ? 'feedbackRetry' : 'feedbackEdit');
      // The theme's capture-phase Escape handler must not close the calculator underneath.
      this.feedbackParentDialog = this.closest('savings-calculator-dialog');
      this.feedbackParentDialog?.focusTrap.pause();
      feedback.showModal();
    }

    buildRecommendationPayload() {
      const formData = new FormData(this.form);
      const type = String(formData.get('type') || '');
      const has_existing_solar = formData.get('has_existing_solar') === 'true';
      const inputMode = String(formData.get('input_mode') || 'annual_usage');
      const consumption = { input_mode: inputMode };
      const tariff = { type };
      const solar = { has_existing_solar };

      if (inputMode === 'monthly_bill') {
        consumption.monthly_bill_eur = Number(formData.get('monthly_bill_eur'));
      } else {
        consumption.annual_usage_kwh = Number(formData.get('annual_usage_kwh'));
      }

      if (type === 'flat') {
        tariff.flat_rate_eur_per_kwh = Number(formData.get('flat_rate_eur_per_kwh'));
      } else if (type === 'day_night') {
        tariff.day_rate_eur_per_kwh = Number(formData.get('day_rate_eur_per_kwh'));
        tariff.night_rate_eur_per_kwh = Number(formData.get('night_rate_eur_per_kwh'));
      }

      if (has_existing_solar) {
        solar.existing_capacity_wp = Number(formData.get('existing_capacity_wp'));
      }

      const payload = {
        location: {
          country_code: String(formData.get('country_code') || ''),
          postcode: String(formData.get('postcode') || '').trim()
        },
        consumption,
        tariff,
        solar,
        preference: {
          objective: String(formData.get('objective') || '')
        }
      };
      if (this.customSetupRequest !== null) {
        payload.custom_setup = { ...this.customSetupRequest };
      }
      return payload;
    }

    async requestRecommendation(payload, signal) {
      const appProxyPath = (this.dataset.appProxyPath || '/apps/im-chat').replace(/\/+$/, '');
      const url = `${appProxyPath}${RECOMMENDATION_PATH}?source=dtc`;
      // 请求推荐结果；取消请求交给 calculate 处理，不包装成网络错误。
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal
      }).catch((cause) => {
        if (cause.name === 'AbortError') throw cause;
        throw this.createRecommendationError('NETWORK_ERROR', 'Recommendation network request failed', {
          category: 'service', retryable: true, requestUrl: url, cause
        });
      });

      // 读取 JSON。HTTP 错误也可能携带业务错误码和字段校验信息。
      const diagnostics = { requestUrl: url, httpStatus: response.status };
      let result;
      try {
        result = await response.json();
      } catch (cause) {
        if (cause.name === 'AbortError') throw cause;
        throw this.createRecommendationError('INVALID_RECOMMENDATION_RESPONSE', 'Invalid JSON response', {
          ...diagnostics, category: response.ok ? 'response' : 'service', reason: 'invalid_json', cause
        });
      }
      const envelope = result?.data;
      const details = {
        ...diagnostics,
        requestId: envelope?.request_id,
        errors: Array.isArray(envelope?.errors) ? envelope.errors : [],
        category: response.status === 422 ? 'validation' : 'service',
        retryable: response.status !== 422
      };
      const message = result?.msg || envelope?.message || 'Recommendation request failed';

      // 依次校验 HTTP 状态、网关响应、算法业务响应。
      if (!response.ok) {
        throw this.createRecommendationError(envelope?.code || 'HTTP_ERROR', message, details);
      }
      // 检查接口中字段是否合法
      if (!result || typeof result !== 'object' || Array.isArray(result)
        || typeof result.code !== 'number' || !Number.isFinite(result.code)) {
        throw this.createRecommendationError('INVALID_RECOMMENDATION_RESPONSE', 'Invalid recommendation envelope', {
          ...details, category: 'response', reason: 'invalid_result_envelope'
        });
      }
      if (result.code !== 0) {
        throw this.createRecommendationError('PROXY_ERROR', message, details);
      }
      if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)
        || typeof envelope.code !== 'string' || !envelope.code.trim()) {
        throw this.createRecommendationError('INVALID_RECOMMENDATION_RESPONSE', 'Invalid business envelope', {
          ...details, category: 'response', reason: 'invalid_business_envelope'
        });
      }
      if (envelope?.code !== 'OK') {
        let category = details.errors.length > 0 ? 'validation' : 'response';
        if (!payload.custom_setup && envelope.code === 'NO_FEASIBLE_SOLUTION' && envelope.data === null) {
          category = 'no-recommendation';
        } else if (payload.custom_setup && response.status === 200 && envelope.code === 'CALCULATION_FAILED'
          && envelope.data === null && details.errors.length === 0) {
          category = 'no-combination';
        }
        throw this.createRecommendationError(envelope.code, message, {
          ...details, category, retryable: category === 'response'
        });
      }

      // 校验可展示的数据，只向调用方返回 equipment 和 estimates 所在的结果对象。
      const issues = this.getRecommendationValidationIssues(envelope.data);
      if (issues.length) throw this.createIncompleteRecommendationError(issues, envelope.request_id);
      return envelope.data;
    }

    getRecommendationValidationIssues(data) {
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return [{ path: 'data.data', reason: 'missing_or_invalid_recommendation_result' }];
      }
      const issues = this.getEquipmentValidationIssues(data.equipment);
      for (const field of ['annual_savings_eur', 'payback_years', 'annual_solar_generation_kwh', 'setup_price_eur']) {
        const value = data.estimates?.[field];
        if (field === 'payback_years' && value === null) continue;
        if (typeof value !== 'number' || !Number.isFinite(value)
          || (field !== 'annual_savings_eur' && value < 0)) {
          issues.push({ path: `data.data.estimates.${field}`, reason: 'missing_or_invalid_estimate', value });
        }
      }
      return issues;
    }

    /**
     * Preserve recommendation fields; only add isBundle for bundle-first
     * details navigation. Convert variant_id at the cart/link call sites.
     */
    async enrichEquipmentWithBundleStatus(equipment, signal) {
      const productRequests = new Map();
      // Only product tags are needed to preserve bundle-first details navigation.
      const getProduct = (handle) => {
        if (!productRequests.has(handle)) {
          const productUrl = `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`;
          productRequests.set(handle, (async () => {
            try {
              const response = await fetch(productUrl, {
                headers: { Accept: 'application/json' },
                signal
              });
              const product = await response.json().catch(() => null);

              if (!response.ok) {
                throw this.createRecommendationError(
                  'PRODUCT_LOAD_FAILED',
                  `Shopify product request failed for handle ${handle} with HTTP ${response.status}`,
                  {
                    handle,
                    productUrl,
                    httpStatus: response.status,
                    reason: 'http'
                  }
                );
              }

              if (!product || typeof product !== 'object' || !Array.isArray(product.tags)) {
                throw this.createRecommendationError(
                  'PRODUCT_LOAD_FAILED',
                  `Shopify product returned an invalid response for handle ${handle}`,
                  {
                    handle,
                    productUrl,
                    httpStatus: response.status,
                    reason: 'invalid_response'
                  }
                );
              }

              return product;
            } catch (error) {
              if (error?.code === 'PRODUCT_LOAD_FAILED') {
                throw error;
              }

              if (error?.name === 'AbortError') {
                const timeoutError = this.createRecommendationError(
                  'PRODUCT_LOAD_FAILED',
                  `Shopify product request timed out for handle ${handle}`,
                  {
                    handle,
                    productUrl,
                    reason: 'timeout',
                    cause: error
                  }
                );
                timeoutError.name = 'AbortError';
                throw timeoutError;
              }

              throw this.createRecommendationError(
                'PRODUCT_LOAD_FAILED',
                `Shopify product request failed for handle ${handle}`,
                {
                  handle,
                  productUrl,
                  reason: 'network',
                  cause: error
                }
              );
            }
          })());
        }

        return productRequests.get(handle);
      };

      return Promise.all(equipment.map(async (item) => {
        const handle = item.handle.trim();
        const product = await getProduct(handle);

        return {
          ...item,
          isBundle: product.tags.some((tag) => typeof tag === 'string' && tag.trim().toLowerCase() === 'bundle')
        };
      }));
    }

    normalizeVariantId(value) {
      return typeof value === 'string'
        ? /^gid:\/\/shopify\/ProductVariant\/([1-9]\d*)$/.exec(value)?.[1] || null
        : null;
    }

    createRecommendationError(code, message, details = {}) {
      const error = new Error(message);
      error.code = code;
      Object.assign(error, details);
      return error;
    }

    createIncompleteRecommendationError(details, requestId) {
      const invalidFields = Array.from(new Set(details.map(({ path }) => path)));

      return this.createRecommendationError(
        'INCOMPLETE_RECOMMENDATION_RESULT',
        'Recommendation API returned incomplete result data',
        {
          reason: 'incomplete_result',
          requestId,
          invalidFields,
          details
        }
      );
    }

    getEquipmentValidationIssues(equipment) {
      if (!Array.isArray(equipment)) {
        return [{
          path: 'data.data.equipment',
          reason: 'missing_or_invalid_equipment',
          value: equipment
        }];
      }

      if (equipment.length === 0) {
        return [{
          path: 'data.data.equipment',
          reason: 'empty_equipment',
          value: equipment
        }];
      }

      return equipment.flatMap((item, index) => {
        const issues = [];
        const addIssue = (field) => issues.push({
          path: `data.data.equipment[].${field}`,
          index,
          reason: `missing_or_invalid_${field}`,
          value: item?.[field]
        });

        for (const field of ['handle', 'sku', 'product_title']) {
          if (typeof item?.[field] !== 'string' || !item[field].trim()) addIssue(field);
        }
        if (!this.normalizeVariantId(item?.variant_id)) addIssue('variant_id');
        if (typeof item?.available !== 'boolean') addIssue('available');
        if (!Number.isInteger(item?.quantity) || item.quantity <= 0) addIssue('quantity');
        for (const field of ['variant_title', 'image_url']) {
          if (item?.[field] != null && typeof item[field] !== 'string') addIssue(field);
        }
        return issues;
      });
    }

    renderRecommendationError(error) {
      if (!this.recommendationErrorDescription) {
        return;
      }

      if (this.lastRecommendationPayload?.custom_setup && error?.code === 'CALCULATION_FAILED') {
        this.recommendationErrorDescription.textContent = this.getString('feedbackCustomDescription');
        return;
      }

      const businessDescription = {
        'no-recommendation': 'feedbackNoRecommendationDescription',
        'no-combination': 'feedbackCustomDescription',
        validation: 'feedbackValidationDescription',
        service: 'feedbackServiceDescription'
      }[error?.category];
      if (businessDescription) {
        this.recommendationErrorDescription.textContent = this.getString(businessDescription);
        return;
      }
      if (error?.code === 'INVALID_RECOMMENDATION_RESPONSE') {
        this.recommendationErrorDescription.textContent = this.getString('invalidRecommendationResponse');
        return;
      }

      if (error?.code === 'INCOMPLETE_RECOMMENDATION_RESULT') {
        this.recommendationErrorDescription.textContent = this.getString('incompleteRecommendationResult');
        return;
      }

      if (error?.code === 'PRODUCT_LOAD_FAILED') {
        const messageKey = {
          http: 'productLoadHttp',
          invalid_response: 'productLoadInvalid',
          network: 'productLoadNetwork',
          timeout: 'productLoadTimeout'
        }[error.reason];

        if (messageKey) {
          this.recommendationErrorDescription.textContent = this.formatString(messageKey, {
            handle: error.handle,
            status: error.httpStatus
          });
          return;
        }
      }

      this.resetRecommendationError();
    }

    resetRecommendationError() {
      if (this.recommendationErrorDescription) {
        this.recommendationErrorDescription.textContent = this.defaultRecommendationErrorDescription;
      }
    }

    formatString(key, replacements = {}) {
      return Object.entries(replacements).reduce((message, [name, value]) => {
        const token = `__${name.replace(/[A-Z]/g, (letter) => `_${letter}`).toUpperCase()}__`;
        return message.split(token).join(value == null ? '' : String(value));
      }, this.getString(key));
    }

    syncConsumptionFields(mode, focusActiveField = false, resetValidation = false) {
      const inputMode = mode === 'monthly_bill' ? 'monthly_bill' : 'annual_usage';
      const inputModeField = this.querySelector('[name="input_mode"]');

      if (inputModeField) {
        inputModeField.value = inputMode;
      }

      this.querySelectorAll('[data-consumption-fields]').forEach((container) => {
        const active = container.dataset.consumptionFields === inputMode;
        container.hidden = !active;
        container.querySelectorAll('input').forEach((field) => {
          field.disabled = !active;

          if (!active || resetValidation) {
            this.resetFieldValidation(field);
          }
        });
      });

      if (focusActiveField) {
        this.querySelector(`[data-consumption-fields="${inputMode}"] input`)?.focus({ preventScroll: true });
      }
    }

    syncConditionalFields({ resetTariffValidation = false } = {}) {
      const tariffType = this.getSelected('type')?.value || 'flat';
      this.querySelectorAll('[data-tariff-fields]').forEach((container) => {
        const active = container.dataset.tariffFields === tariffType;
        container.hidden = !active;
        container.querySelectorAll('input').forEach((field) => {
          field.disabled = !active;

          if (resetTariffValidation) {
            this.resetFieldValidation(field);
          }
        });
      });

      const hasExistingSolar = this.getSelected('has_existing_solar')?.value === 'true';
      const solarFields = this.querySelector('[data-existing-solar-fields]');
      if (solarFields) {
        solarFields.hidden = !hasExistingSolar;
        solarFields.querySelectorAll('input').forEach((field) => {
          field.disabled = !hasExistingSolar;
        });
      }
    }

    resetFieldValidation(field) {
      const error = field.closest('.savings-calculator-field')?.querySelector('[data-field-error]');

      delete field.dataset.touched;
      field.setAttribute('aria-invalid', 'false');

      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    updateContinueState() {
      if (this.dataset.currentView === 'custom-setup') {
        this.syncCustomSetup();
        return;
      }
      if (this.continueButton) {
        this.continueButton.disabled = false;
      }
    }

    syncCustomSetup(changedField = null) {
      const main = this.querySelector('[name="custom_sigenmate_quantity"]');
      if (!main) return false;
      main.max = '3';
      const mainQuantity = Number(main.value);
      const mainValid = main.value.trim() !== '' && Number.isInteger(mainQuantity) && mainQuantity >= 0 && mainQuantity <= 3;
      const units = mainValid ? mainQuantity : 0;
      let adjusted = false;
      const cards = Array.from(this.querySelectorAll('[data-quantity-card]'));

      cards.forEach((card) => {
        const field = card.querySelector('[data-custom-quantity]');
        if (!field) return;
        const dependent = field !== main;
        field.step = card.dataset.quantityCard === 'panels' ? '2' : '1';
        const limit = dependent ? units * (card.dataset.quantityCard === 'panels' ? 8 : 6) : 3;
        field.max = String(limit);
        if (dependent && mainValid && ((units === 0 && field.value !== '0') || (changedField === main && Number(field.value) > limit))) {
          field.value = String(limit);
          adjusted = true;
        }
        const unavailable = dependent && (!mainValid || units === 0);
        card.dataset.quantityDisabled = String(unavailable);
        field.disabled = card.hidden || unavailable;
        const quantity = Number(field.value);
        card.querySelector('[data-quantity-change="-1"]').disabled = field.disabled || !Number.isFinite(quantity) || quantity <= 0;
        card.querySelector('[data-quantity-change="1"]').disabled = field.disabled || !Number.isFinite(quantity) || quantity >= limit;
        field.setAttribute('aria-invalid', String(!field.disabled && !field.checkValidity()));
      });

      const activeFields = this.getCurrentFields();
      const invalid = activeFields.find((field) => !field.checkValidity());
      const allZero = mainValid && units === 0;
      this.querySelector('[data-view="custom-setup"]').dataset.emptySetup = String(allZero);
      const valid = !invalid && !allZero;
      if (changedField) {
        this.customQuantityNotice = '';
        if (adjusted) {
          this.customQuantityNotice = this.getString('customQuantitiesAdjusted');
        } else if (changedField.checkValidity()) {
          const quantity = Number(changedField.value);
          const product = changedField.closest('[data-quantity-card]')?.querySelector('label')?.textContent.trim() || '';
          if (quantity === Number(changedField.max) || quantity === 0) {
            this.customQuantityNotice = this.getString(quantity === 0 ? 'customMinimum' : 'customMaximum')
              .replace('__PRODUCT__', product).replace('__LIMIT__', changedField.max);
          }
        }
      }
      let message = this.customQuantityNotice || '';
      if (invalid) {
        const product = invalid.closest('[data-quantity-card]')?.querySelector('label')?.textContent.trim() || '';
        const rangeKey = invalid.closest('[data-quantity-card]')?.dataset.quantityCard === 'panels' ? 'customPanelQuantityRange' : 'customQuantityRange';
        message = this.getString(rangeKey).replace('__PRODUCT__', product).replace('__LIMIT__', invalid.max);
      } else if (allZero) {
        message = this.getString('customAtLeastOne');
      }
      const status = this.querySelector('[data-custom-quantity-status]');
      if (status) {
        if (status.textContent !== message) status.textContent = message;
        status.hidden = !message;
      }
      if (this.continueButton) this.continueButton.disabled = !valid;
      return valid;
    }

    isCurrentStepValid() {
      const fields = this.getCurrentFields();

      if (this.currentStep === 1) {
        return Boolean(this.getSelected('country_code')) && fields.every((field) => field.checkValidity() && field.value.trim() !== '');
      }

      if (this.currentStep === 2 || this.currentStep === 3) {
        return fields.every((field) => field.checkValidity() && Number(field.value) > 0);
      }

      if (this.currentStep === 4) {
        return Boolean(this.getSelected('has_existing_solar')) && fields.every((field) => field.checkValidity() && field.value.trim() !== '');
      }

      if (this.currentStep === 5) {
        return Boolean(this.getSelected('objective'));
      }

      return false;
    }

    getCurrentFields() {
      if (this.dataset.currentView === 'custom-setup') {
        return Array.from(this.querySelectorAll('[data-custom-quantity]:not(:disabled)'));
      }
      return Array.from(this.querySelectorAll(`[data-step="${this.currentStep}"] input:not([type="radio"]):not([type="hidden"]):not(:disabled)`));
    }

    syncPostcodeValidation(showError = false) {
      const field = this.querySelector('[name="postcode"]');
      if (!field) {
        return;
      }

      const country = this.getSelected('country_code')?.value || 'DE';
      const formats = {
        DE: { pattern: '[0-9]{5}', minLength: 5, maxLength: 5, inputMode: 'numeric', placeholder: '10115' },
        AT: { pattern: '[1-9][0-9]{3}', minLength: 4, maxLength: 4, inputMode: 'numeric', placeholder: '1010' },
        NL: { pattern: '[1-9][0-9]{3} ?[A-Za-z]{2}', minLength: 6, maxLength: 7, inputMode: 'text', placeholder: '1012 AB' }
      };
      const format = formats[country] || formats.DE;
      const countryError = field.dataset[`errorPattern${country.charAt(0)}${country.slice(1).toLowerCase()}`];

      // Clear both bounds before applying the next country. Increasing
      // minLength while the previous, shorter maxLength is still active throws
      // an IndexSizeError and leaves the remaining validation state stale.
      field.removeAttribute('minlength');
      field.removeAttribute('maxlength');
      field.setAttribute('pattern', format.pattern);
      field.setAttribute('minlength', String(format.minLength));
      field.setAttribute('maxlength', String(format.maxLength));
      field.setAttribute('inputmode', format.inputMode);
      field.setAttribute('placeholder', format.placeholder);
      field.dataset.errorPattern = countryError || field.dataset.errorPattern || '';
      this.validateField(field, showError);
    }

    validateField(field, showError = false) {
      const error = field.closest('.savings-calculator-field')?.querySelector('[data-field-error]');
      if (!error) {
        return field.checkValidity();
      }

      let message = '';
      if (showError && !field.validity.valid) {
        if (field.validity.valueMissing) {
          message = field.dataset.errorRequired || '';
        } else if (field.validity.badInput) {
          message = field.dataset.errorNumber || '';
        } else if (field.validity.rangeUnderflow || field.validity.rangeOverflow) {
          message = field.dataset.errorRange || '';
        } else if (field.validity.stepMismatch) {
          message = field.dataset.errorStep || field.dataset.errorNumber || '';
        } else if (field.validity.patternMismatch || field.validity.tooShort || field.validity.tooLong) {
          message = field.dataset.errorPattern || '';
        }
      }

      error.textContent = message;
      error.hidden = !message;
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
      return field.checkValidity();
    }

    getSelected(name) {
      return this.querySelector(`input[name="${name}"]:checked`);
    }

    renderRecommendation(data, equipmentWithBundleStatus) {
      const estimates = data.estimates || {};
      const annualSavings = this.querySelector('[data-annual-savings]');
      const paybackYears = this.querySelector('[data-payback-years]');
      const solarGeneration = this.querySelector('[data-solar-generation]');
      const setupPrice = this.querySelector('[data-setup-price]');

      if (annualSavings) {
        annualSavings.textContent = this.formatCurrency(estimates.annual_savings_eur);
      }
      if (paybackYears) {
        paybackYears.textContent = estimates.payback_years === null ? '—' : `~${this.formatNumber(estimates.payback_years, 1)}`;
        const unit = this.querySelector('[data-payback-unit]');
        if (unit) unit.hidden = estimates.payback_years === null;
      }
      if (solarGeneration) {
        solarGeneration.textContent = this.formatNumber(estimates.annual_solar_generation_kwh);
      }
      if (setupPrice) {
        setupPrice.textContent = this.formatCurrency(estimates.setup_price_eur);
      }

      this.renderEquipment(equipmentWithBundleStatus);
      this.hideCartStatus();
      this.updateCartActions();
      this.updateSetupDetailsLink();
    }

    // Display fields come directly from equipment: product_title -> title/alt,
    // variant_title -> specification (fallback: sku), image_url -> image,
    // quantity -> count. Trimming/defaults are applied only when rendering.
    renderEquipment(equipmentWithBundleStatus) {
      const rows = Array.isArray(equipmentWithBundleStatus) ? equipmentWithBundleStatus : [];
      const equipmentList = this.querySelector('[data-equipment-list]');
      const setup = equipmentList?.closest('.savings-calculator-result__setup');
      const productList = this.querySelector('.savings-calculator-result__products');

      if (setup) {
        setup.hidden = rows.length === 0;
      }
      if (productList) {
        productList.hidden = rows.length === 0;
      }

      if (equipmentList) {
        equipmentList.replaceChildren(...rows.map(({ product_title, variant_title, quantity, sku }) => {
          const row = document.createElement('div');
          const term = document.createElement('dt');
          const description = document.createElement('dd');
          const specification = document.createElement('span');
          const count = document.createElement('strong');

          const variantTitle = variant_title?.trim() || '';
          term.textContent = product_title.trim();
          specification.textContent = variantTitle && variantTitle !== 'Default Title' ? variantTitle : sku.trim();
          term.dataset.overflowTooltip = '';
          specification.dataset.overflowTooltip = '';
          count.textContent = `×${quantity}`;
          description.append(specification, count);
          row.append(term, description);
          return row;
        }));
      }

      if (productList) {
        const templates = this.equipmentCardTemplates || [];
        const cards = templates.length > 0 ? rows.map((item, index) => {
          const card = templates[index % templates.length].cloneNode(true);
          const media = card.querySelector('.savings-calculator-result__product-media');
          const name = card.querySelector('[data-equipment-name]');
          const quantity = card.querySelector('[data-equipment-quantity]');

          card.hidden = false;
          if (media) {
            media.querySelectorAll('img, .savings-calculator-result__product-placeholder').forEach((element) => element.remove());

            if (item.image_url?.trim()) {
              const image = document.createElement('img');
              image.src = item.image_url.trim();
              image.alt = item.product_title.trim();
              image.loading = 'lazy';
              media.insertBefore(image, quantity || null);
            } else {
              const placeholder = document.createElement('span');
              placeholder.className = 'savings-calculator-result__product-placeholder';
              placeholder.setAttribute('aria-hidden', 'true');
              media.insertBefore(placeholder, quantity || null);
            }
          }
          if (name) name.textContent = item.product_title.trim();
          if (quantity) quantity.textContent = `×${item.quantity}`;
          return card;
        }) : [];

        productList.replaceChildren(...cards);
      }

      this.scheduleCarouselSync(true);
      this.scheduleOverflowTooltipSync();
    }

    scheduleOverflowTooltipSync() {
      window.cancelAnimationFrame(this.overflowTooltipFrame);
      this.overflowTooltipFrame = window.requestAnimationFrame(() => {
        this.overflowTooltipFrame = null;
        this.syncOverflowTooltips();
      });
    }

    syncOverflowTooltips() {
      this.querySelectorAll('[data-overflow-tooltip]').forEach((element) => {
        this.syncOverflowTooltipTarget(element);
      });

      if (this.activeOverflowTooltipTarget && !this.activeOverflowTooltipTarget.hasAttribute('data-overflow-tooltip-active')) {
        this.hideOverflowTooltip();
      }
    }

    syncOverflowTooltipTarget(element) {
      const isOverflowing = element.scrollWidth > element.clientWidth + 1;
      element.toggleAttribute('data-overflow-tooltip-active', isOverflowing);
      element.removeAttribute('title');

      if (element.hasAttribute('data-overflow-tooltip-focusable')) {
        if (isOverflowing) {
          element.tabIndex = 0;
        } else {
          element.removeAttribute('tabindex');
        }
      }

      return isOverflowing;
    }

    handleOverflowTooltipEnter(event) {
      const target = event.target.closest('[data-overflow-tooltip]');

      if (
        !target
        || !this.contains(target)
        || event.pointerType === 'touch'
        || !this.syncOverflowTooltipTarget(target)
      ) {
        return;
      }

      this.showOverflowTooltip(target);
    }

    handleOverflowTooltipLeave(event) {
      const target = event.target.closest('[data-overflow-tooltip-active]');

      if (!target || target.contains(event.relatedTarget)) {
        return;
      }

      this.hideOverflowTooltip();
    }

    handleOverflowTooltipFocus(event) {
      const target = event.target.closest('[data-overflow-tooltip][data-overflow-tooltip-focusable]');

      if (target && this.contains(target) && this.syncOverflowTooltipTarget(target)) {
        this.showOverflowTooltip(target);
      }
    }

    handleOverflowTooltipBlur(event) {
      if (event.target.closest('[data-overflow-tooltip-focusable]')) {
        this.hideOverflowTooltip();
      }
    }

    showOverflowTooltip(target) {
      if (!this.overflowTooltip) {
        return;
      }

      const text = target.textContent.trim();
      if (!text) {
        return;
      }

      this.activeOverflowTooltipTarget = target;
      this.overflowTooltip.textContent = text;
      this.overflowTooltip.hidden = false;

      const targetRect = target.getBoundingClientRect();
      const tooltipRect = this.overflowTooltip.getBoundingClientRect();
      const viewportMargin = 12;
      const gap = 8;
      const centeredLeft = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
      const left = Math.min(
        window.innerWidth - tooltipRect.width - viewportMargin,
        Math.max(viewportMargin, centeredLeft)
      );
      const preferredTop = targetRect.top - tooltipRect.height - gap;
      const top = preferredTop >= viewportMargin ? preferredTop : targetRect.bottom + gap;

      this.overflowTooltip.style.left = `${left}px`;
      this.overflowTooltip.style.top = `${top}px`;
    }

    hideOverflowTooltip() {
      this.activeOverflowTooltipTarget = null;

      if (this.overflowTooltip) {
        this.overflowTooltip.hidden = true;
      }
    }

    updateCartActions() {
      if (!this.addToCartButton) {
        return;
      }

      const hasEquipment = this.equipmentWithBundleStatus.length > 0;
      const hasUnavailableItem = this.equipmentWithBundleStatus.some((item) => !item.available);
      this.addToCartButton.disabled = this.cartSubmitting || !hasEquipment || hasUnavailableItem;
      this.addToCartButton.setAttribute('aria-busy', this.cartSubmitting ? 'true' : 'false');

      if (hasEquipment && hasUnavailableItem) {
        this.showCartStatus(this.getString('cartUnavailable'));
      }
    }

    updateSetupDetailsLink() {
      if (!this.setupDetailsLink) {
        return;
      }

      const bundle = this.equipmentWithBundleStatus.find((item) => item.isBundle);
      const targetProduct = bundle || this.equipmentWithBundleStatus.find((item) => !item.isBundle);
      const variantId = this.normalizeVariantId(targetProduct?.variant_id);
      this.setupDetailsLink.href = targetProduct
        ? `${window.Shopify.routes.root}products/${encodeURIComponent(targetProduct.handle.trim())}?variant=${encodeURIComponent(variantId)}`
        : window.Shopify.routes.root;
    }

    getCartItems() {
      const itemsByVariant = new Map();

      this.equipmentWithBundleStatus.forEach(({ variant_id, quantity }) => {
        const id = this.normalizeVariantId(variant_id);
        itemsByVariant.set(id, (itemsByVariant.get(id) || 0) + quantity);
      });

      return Array.from(itemsByVariant, ([id, quantity]) => ({ id, quantity }));
    }

    async ensureCartDrawer() {
      let drawer = document.getElementById('cart-drawer');
      if (!drawer) {
        // Standalone calculator pages omit the header/overlay groups.
        // Load the configured homepage drawer, preserving its section ID/settings.
        const url = new URL(window.Shopify.routes.root, window.location.origin);
        const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
        const response = await fetch(url.href, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(this.getString('cartError'));
        const content = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = content.querySelector('#cart-drawer')?.closest('.shopify-section');
        if (!section) throw new Error(this.getString('cartError'));
        document.body.append(document.importNode(section, true));
        drawer = document.getElementById('cart-drawer');
      }
      if (typeof drawer?.show !== 'function') throw new Error(this.getString('cartError'));
      return drawer;
    }

    async addSetupToCart() {
      if (this.cartSubmitting || this.equipmentWithBundleStatus.length === 0) {
        return;
      }

      if (this.equipmentWithBundleStatus.some((item) => !item.available)) {
        this.showCartStatus(this.getString('cartUnavailable'));
        return;
      }

      this.cartSubmitting = true;
      this.hideCartStatus();
      this.updateCartActions();

      try {
        // Mount before preparing sections so the drawer can request its refresh.
        const cartDrawer = await this.ensureCartDrawer();
        const sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: this.getCartItems(),
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        });
        const responseJson = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(responseJson?.description || responseJson?.message || this.getString('cartError'));
        }

        const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        const cart = await cartResponse.json().catch(() => null);

        if (!cartResponse.ok || !cart) {
          throw new Error(this.getString('cartError'));
        }

        cart.sections = responseJson?.sections || {};
        const calculatorDialog = this.closest('savings-calculator-dialog');

        if (calculatorDialog?.open) {
          await calculatorDialog.hide();
        }

        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: Array.isArray(responseJson?.items) ? responseJson.items : [],
            cart,
            blockCartDrawerOpening: true
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart
          }
        }));

        await new Promise((resolve) => window.setTimeout(resolve, 0));

        await cartDrawer.show();
      } catch (error) {
        console.error('[SigenMate calculator] Add setup to cart failed', error);
        this.showToast(error.message || this.getString('cartError'));
      } finally {
        this.cartSubmitting = false;
        this.updateCartActions();
      }
    }

    showCartStatus(message) {
      if (!this.cartStatus || !message) {
        return;
      }

      this.cartStatus.textContent = message;
      this.cartStatus.hidden = false;
    }

    hideCartStatus() {
      if (this.cartStatus) {
        this.cartStatus.hidden = true;
        this.cartStatus.textContent = '';
      }
    }

    formatCurrency(value, maximumFractionDigits = 0) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '—';
      }

      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits
      }).format(number);
    }

    formatNumber(value, maximumFractionDigits = 0) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '—';
      }

      return number.toLocaleString(document.documentElement.lang || undefined, { maximumFractionDigits });
    }

    renderResultAnswers() {
      const target = this.querySelector('[data-result-answers]');
      if (!target) {
        return;
      }

      const country = this.getSelected('country_code')?.dataset.display || '';
      const postcode = this.querySelector('[name="postcode"]')?.value.trim() || '';
      const inputMode = this.querySelector('[name="input_mode"]')?.value || 'annual_usage';
      const consumption = inputMode === 'monthly_bill'
        ? `${this.formatCurrency(this.querySelector('[name="monthly_bill_eur"]')?.value, 2)} / ${this.getString('month')}`
        : `${this.formatNumber(this.querySelector('[name="annual_usage_kwh"]')?.value)} kWh/${this.getString('year')}`;
      const tariff = this.getSelected('type')?.dataset.display || '';
      const solar = this.getSelected('has_existing_solar')?.dataset.display || '';
      const priority = this.getSelected('objective')?.dataset.display || '';
      const values = [
        { key: 'location', value: `${country} · ${postcode}` },
        { key: 'consumption', value: consumption },
        { key: 'tariff', value: tariff },
        { key: 'solar', value: solar },
        { key: 'priority', value: priority }
      ];

      if (this.lastRecommendationPayload?.custom_setup) {
        values.push({ key: 'custom-setup', value: this.getString('customSetupLabel') });
      }

      target.replaceChildren(...values.map(({ key, value }) => {
        const item = document.createElement('span');
        item.className = 'savings-calculator-result__answer';
        item.dataset.answer = key;

        if (this.dataset.displayMode === 'dialog') {
          item.dataset.overflowTooltip = '';
          item.dataset.overflowTooltipFocusable = '';
        }

        item.textContent = value;
        return item;
      }));

      this.scheduleOverflowTooltipSync();
    }

    getString(key) {
      return this.querySelector('template[data-i18n]')?.content.querySelector(`[data-key="${key}"]`)?.textContent.trim() || '';
    }

    showToast(message) {
      if (!this.toast || !message) {
        return;
      }

      window.clearTimeout(this.toastTimer);
      this.toast.textContent = message;
      this.toast.hidden = false;
      this.toastTimer = window.setTimeout(() => this.hideToast(), 3200);
    }

    hideToast() {
      if (this.toast) {
        this.toast.hidden = true;
        this.toast.textContent = '';
      }
    }
  }

  window.customElements.define('savings-calculator', SavingsCalculator);
}

if (!window.customElements.get('savings-calculator-dialog')) {
  class SavingsCalculatorDialog extends DialogElement {
    connectedCallback() {
      super.connectedCallback();

      if (this.savingsDialogAbortController) {
        return;
      }

      this.savingsDialogAbortController = new AbortController();
      const signal = this.savingsDialogAbortController.signal;
      this.calculator = this.querySelector('savings-calculator');

      this.querySelector('[data-dialog-dismiss]')?.addEventListener('click', () => this.hide(), { signal });
      this.addEventListener('dialog:after-hide', () => this.calculator?.resetCalculator(), { signal });
      this.addEventListener('dialog:after-show', () => this.calculator?.updateContinueState(), { signal });
      this.mobileViewport = window.matchMedia('(max-width: 699px)');
      this.mobileViewport.addEventListener('change', () => this.openMobilePage(), { signal });
      this.addEventListener('dialog:after-show', () => this.openMobilePage(), { signal });
      this.openMobilePage();
    }

    openMobilePage() {
      if (!this.open || !this.mobileViewport.matches || this.mobileRedirecting || !this.dataset.mobilePageUrl) return;

      const url = new URL(this.dataset.mobilePageUrl, window.location.href);
      if (url.origin !== window.location.origin) return;
      this.mobileRedirecting = true;
      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

      try {
        const token = window.crypto.randomUUID();
        window.sessionStorage.setItem(MOBILE_HANDOFF_PREFIX + token, JSON.stringify({
          url: url.href,
          savedAt: Date.now(),
          fields: Object.fromEntries(Array.from(this.calculator.form.elements)
            .filter((field) => field.name && (field.type !== 'radio' || field.checked))
            .map((field) => [field.name, field.value])),
          step: this.calculator.currentStep,
          view: this.calculator.dataset.currentView
        }));
        url.searchParams.set(MOBILE_HANDOFF_PARAM, token);
      } catch {
        // Still switch to the correct page when form persistence is unavailable.
      }
      window.location.assign(url.href);
    }

    disconnectedCallback() {
      this.savingsDialogAbortController?.abort();
      this.savingsDialogAbortController = null;
      super.disconnectedCallback();
    }

    get shouldLock() {
      return true;
    }

    _showTransition() {
      return Promise.resolve();
    }

    _hideTransition() {
      return Promise.resolve();
    }
  }

  window.customElements.define('savings-calculator-dialog', SavingsCalculatorDialog);
}
